import React, { useState, useRef, useEffect } from 'react';
import { Filter, ChevronDown, Check } from 'lucide-react';
import { useAllCompanies, CompanyOption } from '../hooks/useAllCompanies';
import { useResellerCompany } from '../contexts/ResellerCompanyContext';
import { useCompanyScope } from '../hooks/useCompanyScope';

interface ResellerCompanyFilterProps {
  /** 'header' (default): pill + dropdown for the top bar — hidden on mobile widths.
   *  'drawer': full-width list for the mobile sidebar drawer. */
  variant?: 'header' | 'drawer';
}

/**
 * Narrows the reseller's merged "All my companies" view down to one of their companies
 * (or all). Styled to match ResellerCompanySwitcher, and shown only while that merged
 * view is active. The backend only honours the filter for companies the reseller owns.
 */
const ResellerCompanyFilter: React.FC<ResellerCompanyFilterProps> = ({ variant = 'header' }) => {
  const { resellerAll } = useCompanyScope();
  const { companyFilter, setCompanyFilter } = useResellerCompany();
  const { data } = useAllCompanies();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!resellerAll) return null;
  const companies: CompanyOption[] = data || [];
  const current = companyFilter == null ? null : companies.find((c) => c.tenantId === companyFilter) || null;

  const choose = (id: number | null) => {
    setOpen(false);
    if (id === companyFilter) return;
    setCompanyFilter(id);
  };

  if (variant === 'drawer') {
    // Collapsible so it stays a single row by default — expanded it would double the
    // company list already shown by the switcher above and push the nav out of view.
    return (
      <div className="px-3 pb-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center justify-between w-full px-3 py-2.5 text-sm text-left rounded-lg text-[#0E131B] hover:bg-gray-50 transition-colors"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Filter className="h-4 w-4 flex-shrink-0 text-[#0055a9]" />
            <span className="flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-400">Filter</span>
            <span className="truncate font-medium">{current ? current.name : 'All companies'}</span>
          </span>
          <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="mt-0.5 space-y-0.5">
            <button
              onClick={() => choose(null)}
              className={`flex items-center justify-between w-full pl-9 pr-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
                companyFilter == null ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
              }`}
            >
              <span className="truncate">All companies</span>
              {companyFilter == null && <Check className="h-4 w-4 flex-shrink-0" />}
            </button>
            {companies.map((c) => {
              const active = companyFilter === c.tenantId;
              return (
                <button
                  key={c.tenantId}
                  onClick={() => choose(c.tenantId)}
                  className={`flex items-center justify-between w-full pl-9 pr-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
                    active ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate">{c.name}</span>
                  {active && <Check className="h-4 w-4 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Filter by company"
        className="flex items-center gap-2 h-11 px-3 rounded-xl border bg-[#F2F2F7] border-[#E8EBF1] text-[#1B202A] hover:bg-[#EAEBF0] transition-colors"
      >
        <Filter className="h-5 w-5 flex-shrink-0 text-[#0055a9]" />
        <span className="text-sm font-semibold truncate max-w-[9rem] hidden sm:block">
          {current ? current.name : 'All companies'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-[80px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[240px] bg-white rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] border border-[#E1E6EF] py-1 z-[1002] max-h-[70vh] overflow-y-auto">
          <button
            onClick={() => choose(null)}
            className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
              companyFilter == null ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
            }`}
          >
            <span className="flex items-center gap-2"><Filter className="h-4 w-4" /> All companies</span>
            {companyFilter == null && <Check className="h-4 w-4 flex-shrink-0" />}
          </button>
          <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            My Companies
          </div>
          {companies.map((c) => {
            const active = companyFilter === c.tenantId;
            return (
              <button
                key={c.tenantId}
                onClick={() => choose(c.tenantId)}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                  active ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
                }`}
              >
                <span className="truncate">{c.name}</span>
                {active && <Check className="h-4 w-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ResellerCompanyFilter;
